import { useCallback, useEffect, useContext, useState } from "react";
import { VecvizChartConfig } from "./VecVizConfig";
import { StoreContext } from "../../../store/Store";
import { LocalStoreContext } from "../store/LocalStore";
import { RefContext } from "../store/RefContext";
import { applyOverrides, getFilteredData } from "../../../utils/dataFunctions";
import { config as VecVizConfig } from "../../../d3charts/vecviz/config";
import { actionTypes } from "../../../store/storeTypes";
import { Loader } from "../../../components/loader/Loader";

export const StudyChart = () => {
  const [store, dispatch] = useContext(StoreContext);
  const [lstore] = useContext(LocalStoreContext);
  const refs = useContext(RefContext);

  const { activeLineName, modelDateItem, vectorSet2, filter } = lstore;

  const [vectorStudyData, setVectorStudyData] = useState();
  const [pxRange, setPxRange] = useState();
  const [vectorStudyConfig, setVectorStudyConfig] = useState();
  
  // Dummy state to force re-renders for layout fix
  const [, forceUpdate] = useState({});

  if (store) {
    //do nothing
  }
  useEffect(() => {
    refs.layoutRefs.current["brush"]["VectorSetStudy"] = {
      x: [],
      y: [],
    };
  }, [refs.layoutRefs]);

  useEffect(() => {
    if (vectorSet2.rawdata.length > 0 && modelDateItem) {
      const modelDateValue = modelDateItem.value;
      const VectorSetStudyFilter = {
        ...filter,
        VecEvents: [],
        VecLevel: [],
        VecDates: [],
      };

      const vectorStudyConfig = applyOverrides(VecVizConfig, {
        brush: { y: { active: true }, x: { active: true } },

        colourMapping: {
          "vs1 thru vs20": {
            stroke: "#3C3C3C",
            strokeWidth: 2,
          },
          "vs21 thru vs60": {
            stroke: "#787878",
            strokeWidth: 2,
          },
          "vs61 thru vs325": {
            stroke: "#B4B4B4",
            strokeWidth: 2,
          },
        },

        header: {
          text: `${refs.vectorset2CurrentTicker.current} VectorSet Study`,
        },

        ignoreVecLevelFilter: true,
        ignoreVecEventsFilter: true,
        ignoreVecDatesFilter: true,
        horizontalLines: {
          [modelDateValue]: {
            line: { stroke: "black", strokeWidth: 1 },
            label: {
              textAnchor: "start",

              colour: "grey",
              fontSize: 10,
              fontWeight: 400,
              dy: "-0.5em",
              dx: "0.5em",
            },
          },
        },
      });
      setVectorStudyConfig(vectorStudyConfig);

      const vectorStudyDataRaw = getFilteredData(
        vectorSet2.rawdatacombined,
        {
          name: activeLineName,
          style: vectorStudyConfig.activeLine.style,
          respond: vectorStudyConfig.activeLine.respond,
        },
        VectorSetStudyFilter,
        vectorStudyConfig.colourMapping,
        vectorStudyConfig.valueFilter,
        vectorStudyConfig.ignoreVectorsInVectorSetFilter,

        vectorStudyConfig.ignoreVecLevelFilter,
        vectorStudyConfig.ignoreVecEventsFilter,
        vectorStudyConfig.ignoreVecDatesFilter
      );
      
      setVectorStudyData(vectorStudyDataRaw);

      // --- START PERFORMANCE OPTIMIZATION ---
      let dataMin = Infinity;
      let dataMax = -Infinity;

      if (vectorStudyDataRaw) {
        vectorStudyDataRaw.forEach((series) => {
          if (series.values) {
            series.values.forEach((point) => {
              if (point.px < dataMin) dataMin = point.px;
              if (point.px > dataMax) dataMax = point.px;
            });
          }
        });
      }

      if (dataMin === Infinity) dataMin = 0;
      if (dataMax === -Infinity) dataMax = 100;

      setPxRange({ min: dataMin, max: dataMax });
      // --- END PERFORMANCE OPTIMIZATION ---
      
      // --- Auto-Refresh Fix ---
      setTimeout(() => {
        forceUpdate({});
      }, 500);
      // -----------------------------
    }
  }, [
    vectorSet2,
    activeLineName,
    filter,
    refs.layoutRefs,
    modelDateItem,
    refs.vectorset2CurrentTicker,
  ]);

  const setTooltip = useCallback(
    (tooltip) => {
      dispatch({ type: actionTypes.SET_TOOLTIP, payload: tooltip });
    },
    [dispatch]
  );

  const setBrushRef = useCallback(
    (ext, axis) => {
      refs.layoutRefs.current["brush"]["VectorSetStudy"][axis] = ext;
    },
    [refs.layoutRefs]
  );

  return vectorStudyConfig && pxRange && vectorStudyData ? (
    <VecvizChartConfig
      data={vectorStudyData}
      pxRange={pxRange}
      width={refs.componentSize.current["VectorSetStudy"].width}
      height={refs.componentSize.current["VectorSetStudy"].height}
      brushRef={refs.layoutRefs.current["brush"]["VectorSetStudy"]}
      setBrushRef={setBrushRef}
      setTooltip={setTooltip}
      config={vectorStudyConfig}
      lRef={refs.studyRef}
    />
  ) : (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column", // Stack loader and text
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Loader />
      {/* WARNING TEXT ONLY ON LOADING SCREEN */}
      <div style={{ marginTop: "10px", color: "grey", fontSize: "12px" }}>
        Initial load is slow, subsequent loads are fast...
      </div>
    </div>
  );
};